import React, { useEffect } from "react";
import { useFormContext, useFieldArray, Controller } from "react-hook-form";
import { Card, Col, FormControl, FormGroup, Nav, Tab } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { mapLocaleToCountry } from "../../witadmin/Common/utils";
import {
  ErrorField,
  StyledContainer,
  StyledTranslationCard,
} from "../../shared/widgets/TranslationComponent/styles";
import "flag-icon-css/css/flag-icon.css";

// All locales supported by the engine
const ENGINE_SUPPORTED_LOCALES = [
  "eu",
  "ca",
  "da",
  "de",
  "nl",
  "en",
  "es",
  "fr",
  "hu",
  "it",
  "ja",
  "ko",
  "pt",
  "ro",
  "ru",
  "zh",
];

export interface BrandTranslation {
  locale: string;
  name?: string;
  description?: string;
}

export interface BrandTranslationForm {
  translationsList: BrandTranslation[];
}

function getLocaleCountryMap(locales: string[]): Record<string, string> {
  return locales.reduce<Record<string, string>>((prev, locale) => {
    prev[locale] = mapLocaleToCountry(locale);
    return prev;
  }, {});
}

export const BrandTranslationComponent = ({
  defaultValues,
  disabled = false,
}: {
  defaultValues?: BrandTranslation[];
  disabled?: boolean;
}) => {
  const localeCountryMap = React.useMemo(() => getLocaleCountryMap(ENGINE_SUPPORTED_LOCALES), []);

  const {
    control,
    formState: { errors },
  } = useFormContext<BrandTranslationForm>();

  const { fields, replace } = useFieldArray({
    name: "translationsList",
  });

  // Track what data we've initialized with to detect actual changes
  const previousDataRef = React.useRef<string>("");

  // Initialize/update the array when defaultValues arrive or change
  useEffect(() => {
    if (!defaultValues || defaultValues.length === 0) {
      return;
    }

    const currentDataKey = JSON.stringify(defaultValues);

    if (previousDataRef.current === currentDataKey) {
      return;
    }

    previousDataRef.current = currentDataKey;

    const translationsByLocale = new Map<string, BrandTranslation>();

    defaultValues.forEach(trans => {
      if (trans && trans.locale) {
        translationsByLocale.set(trans.locale, trans);
      }
    });

    const orderedTranslations = ENGINE_SUPPORTED_LOCALES.map(locale => {
      const existing = translationsByLocale.get(locale);

      if (existing) {
        return {
          locale: locale,
          name: existing.name || "",
          description: existing.description || "brand description",
        };
      } else {
        return {
          locale: locale,
          name: "",
          description: "brand description",
        };
      }
    });

    replace(orderedTranslations);
  }, [defaultValues, replace]);

  const tabs: [React.ReactElement, React.ReactElement][] = fields.map((field, index) => {
    const locale = ENGINE_SUPPORTED_LOCALES[index];

    return [
      <Nav.Item key={field.id}>
        <Nav.Link eventKey={`locale-${locale}`}>
          <span className={`flag-icon flag-icon-${localeCountryMap[locale]}`} />
        </Nav.Link>
      </Nav.Item>,
      <Tab.Pane key={field.id} eventKey={`locale-${locale}`}>
        <Controller
          name={`translationsList.${index}`}
          control={control}
          render={({ field: controllerField }) => {
            const currentValue = controllerField.value || {
              locale,
              name: "",
              description: "brand description",
            };

            return (
              <FormGroup as={Col}>
                <FormControl
                  disabled={disabled}
                  value={currentValue.name || ""}
                  onChange={e => {
                    const newName = e.target.value;
                    const newValue = {
                      locale: locale,
                      name: newName,
                      description: "brand description",
                    };
                    controllerField.onChange(newValue);
                  }}
                />
                {errors?.translationsList && errors?.translationsList[index]?.name && (
                  <ErrorField>
                    <FormattedMessage
                      id="translation.required"
                      defaultMessage="This field is required"
                    />
                  </ErrorField>
                )}
              </FormGroup>
            );
          }}
        />
      </Tab.Pane>,
    ];
  });

  return (
    <StyledContainer>
      {!!errors?.translationsList && (
        <ErrorField className="mb-2">
          <FormattedMessage
            id="translation.tabsErrors"
            defaultMessage="There are errors in some tabs"
          />
        </ErrorField>
      )}

      <Tab.Container defaultActiveKey={`locale-${ENGINE_SUPPORTED_LOCALES[0]}`}>
        <StyledTranslationCard>
          <Card.Header>
            <Nav variant="tabs">{tabs.map(t => t[0])}</Nav>
          </Card.Header>
          <Card.Body className="px-0 py-2">
            <Tab.Content>{tabs.map(t => t[1])}</Tab.Content>
          </Card.Body>
        </StyledTranslationCard>
      </Tab.Container>
    </StyledContainer>
  );
};
